import React, { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { 
  FiHome, 
  FiUsers, 
  FiFileText, 
  FiImage, 
  FiLogOut,
  FiMenu,
  FiX,
  FiUser,
  FiEye
} from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('Logged out successfully')
      navigate('/admin/login')
    } catch (error) {
      toast.error('Error logging out')
    }
  }

  const handleViewSite = () => {
    window.open('/', '_blank')
  }

  const navigationItems = [
    { path: '/admin/dashboard', icon: FiHome, label: 'Dashboard' },
    { path: '/admin/content', icon: FiFileText, label: 'Content' },
    { path: '/admin/artists', icon: FiUsers, label: 'Artists' },
    { path: '/admin/media', icon: FiImage, label: 'Media' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-neutral-50 to-purple-50/30 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-col w-64 bg-white border-r border-neutral-200 fixed left-0 top-0 bottom-0">
        {/* Logo */}
        <div className="flex items-center h-16 px-6 border-b border-neutral-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-md flex items-center justify-center shadow-sm">
              <span className="text-white font-semibold text-sm">O</span>
            </div>
            <span className="font-semibold text-neutral-900">Admin Panel</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navigationItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium shadow-sm'
                    : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                }`
              }
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="border-t border-neutral-200 p-3 flex-shrink-0">
          <div className="flex items-center space-x-3 mb-3 px-2">
            <div className="w-8 h-8 bg-neutral-100 rounded-full flex items-center justify-center">
              <FiUser size={14} className="text-neutral-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-neutral-900 truncate">
                {user?.email}
              </p>
              <p className="text-xs text-neutral-500">Admin</p>
            </div>
          </div>
          
          <div className="space-y-1">
            <button
              onClick={handleViewSite}
              className="w-full flex items-center space-x-2 px-3 py-2 text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 rounded-lg transition-all text-sm"
            >
              <FiEye size={16} />
              <span>View Site</span>
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 px-3 py-2 text-neutral-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all text-sm"
            >
              <FiLogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl lg:hidden border-r border-neutral-200 transform transition-transform duration-300 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-neutral-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-md flex items-center justify-center shadow-sm">
              <span className="text-white font-semibold text-sm">O</span>
            </div>
            <span className="font-semibold text-neutral-900">Admin Panel</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-neutral-500 hover:text-neutral-700 p-1"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navigationItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium shadow-sm'
                    : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                }`
              }
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="border-t border-neutral-200 p-3 flex-shrink-0">
          <div className="flex items-center space-x-3 mb-3 px-2">
            <div className="w-8 h-8 bg-neutral-100 rounded-full flex items-center justify-center">
              <FiUser size={14} className="text-neutral-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-neutral-900 truncate">
                {user?.email}
              </p>
              <p className="text-xs text-neutral-500">Admin</p>
            </div>
          </div>
          
          <div className="space-y-1">
            <button
              onClick={handleViewSite}
              className="w-full flex items-center space-x-2 px-3 py-2 text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 rounded-lg transition-all text-sm"
            >
              <FiEye size={16} />
              <span>View Site</span>
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 px-3 py-2 text-neutral-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all text-sm"
            >
              <FiLogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {/* Header */}
        <header className="relative bg-gradient-to-r from-blue-80/100 via-purple-50/50 to-indigo-50/50 border-b border-neutral-200 h-16 flex items-center justify-between px-6 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-neutral-600 hover:text-neutral-900 p-1"
          >
            <FiMenu size={20} />
          </button>
          
          <div className="flex items-center space-x-4 ml-auto">
            <span className="text-xs text-neutral-500 font-medium">
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6 lg:p-8 max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

export default AdminLayout